
import React, { useState, useEffect } from 'react';
import { User, Lock, Mail, Scissors, ArrowLeft, Loader, AlertCircle, Phone, MapPin, Globe } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { API_BASE, getImageUrl } from '../config';

export default function Auth({ onLogin, t, lang, setLang }) {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    firstname: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    preferred_language: lang
  });

  useEffect(() => {
    fetch(`${API_BASE}/settings`)
      .then(res => res.json())
      .then(data => {
         if (data.logo_image) setLogoUrl(data.logo_image);
      })
      .catch(err => console.error("Error loading logo", err));
  }, []);

  const flagUrls = {
    fr: 'https://flagcdn.com/fr.svg',
    en: 'https://flagcdn.com/gb.svg',
    pt: 'https://flagcdn.com/pt.svg'
  };

  const validatePassword = (password) => {
    const regex = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[\W_]).{12,}$/;
    return regex.test(password);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!isLogin && !validatePassword(formData.password)) {
      setError(t.auth.passwordRequirements);
      setLoading(false);
      return;
    }

    const endpoint = isLogin ? '/auth/login' : '/auth/register';
    const payload = isLogin 
      ? { email: formData.email, password: formData.password }
      : formData;

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Error");

      onLogin(data.user);
      navigate(from, { replace: true });

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 via-white to-purple-50 px-4 relative py-12">
      <Link to="/" className="absolute top-6 left-6 flex items-center text-gray-600 hover:text-brand-600 transition-colors">
        <ArrowLeft className="h-5 w-5 mr-1" />
        <span className="font-medium hidden sm:inline">{t.auth.backToHome}</span>
      </Link>

      <div className="absolute top-6 right-6 flex space-x-2">
        {['fr', 'en', 'pt'].map((language) => (
          <button
            key={language}
            onClick={() => setLang(language)}
            className={`${lang === language ? 'bg-gray-100 ring-2 ring-brand-500 transform scale-110' : 'hover:bg-gray-100 opacity-70 hover:opacity-100'} w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 shadow-sm border border-gray-200 overflow-hidden`}
            title={language.toUpperCase()}
          >
            <img 
                src={flagUrls[language]} 
                alt={language} 
                className="w-full h-full object-cover" 
            />
          </button>
        ))}
      </div>

      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md border border-gray-100 my-8">
        <div className="text-center mb-8">
          {logoUrl ? (
             <img src={getImageUrl(logoUrl)} alt="Logo" className="h-40 w-auto mx-auto mb-6 object-contain" />
          ) : (
            <div className="inline-flex items-center justify-center w-20 h-20 bg-brand-100 rounded-full mb-6">
              <Scissors className="h-10 w-10 text-brand-600" />
            </div>
          )}
          <h2 className="text-3xl font-bold text-gray-800">{t.auth.welcome}</h2>
          <p className="text-gray-500 mt-2">{isLogin ? t.auth.loginTitle : t.auth.signupTitle}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center">
            <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div className="flex space-x-4">
              <div className="flex-1 relative">
                <label className="text-sm font-medium text-gray-700 block mb-1">{t.auth.firstname}</label>
                <div className="relative">
                  <User className="absolute inset-y-0 left-0 pl-3 h-full flex items-center pointer-events-none text-gray-400" />
                  <input type="text" name="firstname" value={formData.firstname} onChange={handleChange} required={!isLogin} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all" placeholder={t.auth.firstname} />
                </div>
              </div>
              <div className="flex-1 relative">
                <label className="text-sm font-medium text-gray-700 block mb-1">{t.auth.name}</label>
                <div className="relative">
                  <User className="absolute inset-y-0 left-0 pl-3 h-full flex items-center pointer-events-none text-gray-400" />
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required={!isLogin} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all" placeholder={t.auth.name} />
                </div>
              </div>
            </div>
          )}

          <div className="relative">
            <label className="text-sm font-medium text-gray-700 block mb-1">{t.auth.email}</label>
            <div className="relative">
              <Mail className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all" placeholder="name@example.com" />
            </div>
          </div>

          {!isLogin && (
            <>
              <div className="relative">
                <label className="text-sm font-medium text-gray-700 block mb-1">{t.auth.phone}</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all" placeholder="+33 6 12 34 56 78" />
                </div>
              </div>
              <div className="relative">
                <label className="text-sm font-medium text-gray-700 block mb-1">{t.auth.address}</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input type="text" name="address" value={formData.address} onChange={handleChange} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all" placeholder="123 Rue de la Paix, Paris" />
                </div>
              </div>
              <div className="relative">
                <label className="text-sm font-medium text-gray-700 block mb-1">{t.auth.language}</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <select name="preferred_language" value={formData.preferred_language} onChange={handleChange} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all bg-white">
                    <option value="fr">🇫🇷 Français</option>
                    <option value="en">🇬🇧 English</option>
                    <option value="pt">🇵🇹 Português</option>
                  </select>
                </div>
              </div>
            </>
          )}

          <div className="relative">
            <label className="text-sm font-medium text-gray-700 block mb-1">{t.auth.password}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input type="password" name="password" value={formData.password} onChange={handleChange} required className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all" placeholder="••••••••" />
            </div>
            {!isLogin && <p className="text-[10px] text-gray-500 mt-1">{t.auth.passwordRequirements}</p>}
          </div>

          {isLogin && (
            <div className="text-right -mt-2 mb-4">
              <Link to="/forgot-password" className="text-sm text-brand-600 hover:text-brand-700 hover:underline font-medium transition-all">{t.auth.forgotPassword}</Link>
            </div>
          )}

          <button type="submit" disabled={loading} className="w-full bg-brand-600 text-white py-2.5 rounded-lg font-semibold hover:bg-brand-700 transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-0.5 duration-200 flex justify-center items-center">
            {loading ? <Loader className="animate-spin h-5 w-5" /> : (isLogin ? t.auth.submitLogin : t.auth.submitSignup)}
          </button>
        </form>

        <div className="mt-6 flex flex-col items-center space-y-2 border-t border-gray-100 pt-6">
          <p className="text-sm text-gray-500">{isLogin ? (t.auth.switchSignup.split('?')[0] + '?') : (t.auth.switchLogin.split('?')[0] + '?')}</p>
          <button onClick={() => setIsLogin(!isLogin)} className="text-brand-600 font-bold hover:text-brand-700 transition-all">
            {isLogin ? t.auth.submitSignup : t.auth.submitLogin}
          </button>
        </div>
      </div>
    </div>
  );
}
